import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import Swal from "sweetalert2";
import AdminLayout from "@/components/feature/AdminLayout";
import AdminHeader from "@/components/feature/AdminHeader";
import LoadingState from "@/components/base/LoadingState";
import ErrorState from "@/components/base/ErrorState";
import ModuleGate from "@/components/base/ModuleGate";
import { requireAuth } from "@/middleware/authGuard";
import { supabase } from "@/lib/supabaseClient";
import { getTenantId } from "@/lib/getTenant";
import TemplateCardModal, { type InvTemplate } from "./components/TemplateCardModal";
import ClientTemplateModal, { type ClientTemplate, type SimpleClient } from "./components/ClientTemplateModal";

type Tab = "generic" | "custom";

export default function InvitationTemplatesPage() {
  const navigate = useNavigate();
  const [tenantId, setTenantId]                 = useState<string | null>(null);
  const [tab, setTab]                           = useState<Tab>("generic");
  const [loading, setLoading]                   = useState(true);
  const [error, setError]                       = useState<string | null>(null);

  // Generic templates
  const [templates, setTemplates]               = useState<InvTemplate[]>([]);
  const [tplModalOpen, setTplModalOpen]         = useState(false);
  const [tplSelected, setTplSelected]           = useState<InvTemplate | null>(null);
  const [tplIsNew, setTplIsNew]                 = useState(false);
  const [togglingId, setTogglingId]             = useState<string | null>(null);

  // Client templates
  const [clientTemplates, setClientTemplates]   = useState<ClientTemplate[]>([]);
  const [clients, setClients]                   = useState<SimpleClient[]>([]);
  const [ctModalOpen, setCtModalOpen]           = useState(false);
  const [ctSelected, setCtSelected]             = useState<ClientTemplate | null>(null);
  const [ctIsNew, setCtIsNew]                   = useState(false);
  const [search, setSearch]                     = useState("");

  const fetchAll = useCallback(async (tid: string) => {
    setLoading(true);
    setError(null);
    try {
      const [tplRes, ctRes, clientRes] = await Promise.all([
        supabase
          .from("invitation_templates")
          .select("*")
          .or(`tenant_id.eq.${tid},tenant_id.is.null`)
          .order("sort_order"),
        supabase
          .from("client_invitation_templates")
          .select("*, clients(full_name)")
          .eq("tenant_id", tid)
          .order("created_at", { ascending: false }),
        supabase
          .from("clients")
          .select("id, full_name, email")
          .eq("tenant_id", tid)
          .eq("status", "active")
          .order("full_name"),
      ]);
      if (tplRes.error) throw new Error(tplRes.error.message);
      if (ctRes.error)  throw new Error(ctRes.error.message);

      setTemplates((tplRes.data ?? []) as InvTemplate[]);
      setClientTemplates(
        (ctRes.data ?? []).map((r: Record<string, unknown>) => ({
          ...(r as ClientTemplate),
          client_name: (r.clients as { full_name: string } | null)?.full_name ?? "—",
        }))
      );
      setClients((clientRes.data ?? []) as SimpleClient[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error cargando templates.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      const ok = await requireAuth();
      if (!ok) { navigate("/login"); return; }
      const tid = await getTenantId();
      setTenantId(tid);
      if (tid) fetchAll(tid);
      else setLoading(false);
    };
    init();
  }, [navigate, fetchAll]);

  /* ── Generic template CRUD ── */
  const handleSaveTpl = async (data: Partial<InvTemplate>) => {
    if (!tenantId) return;

    if (tplIsNew) {
      const { data: created, error } = await supabase
        .from("invitation_templates")
        .insert({ ...data, tenant_id: tenantId })
        .select()
        .maybeSingle();
      if (error) { Swal.fire({ title: "Error", text: error.message, icon: "error" }); return; }
      if (created) setTemplates((prev) => [...prev, created as InvTemplate]);
      Swal.fire({ title: "Creado", text: "Template agregado.", icon: "success", timer: 1500, showConfirmButton: false });
    } else if (tplSelected) {
      const { data: updated, error } = await supabase
        .from("invitation_templates")
        .update({ ...data, updated_at: new Date().toISOString() })
        .eq("id", tplSelected.id)
        .select()
        .maybeSingle();
      if (error) { Swal.fire({ title: "Error", text: error.message, icon: "error" }); return; }
      if (updated) setTemplates((prev) => prev.map((t) => t.id === (updated as InvTemplate).id ? updated as InvTemplate : t));
      Swal.fire({ title: "Guardado", icon: "success", timer: 1500, showConfirmButton: false });
    }
    setTplModalOpen(false);
  };

  const handleToggleTpl = async (tpl: InvTemplate) => {
    setTogglingId(tpl.id);
    const { data, error } = await supabase
      .from("invitation_templates")
      .update({ is_active: !tpl.is_active, updated_at: new Date().toISOString() })
      .eq("id", tpl.id)
      .select()
      .maybeSingle();
    if (!error && data) setTemplates((prev) => prev.map((t) => t.id === tpl.id ? data as InvTemplate : t));
    setTogglingId(null);
  };

  const handleDeleteTpl = async (tpl: InvTemplate) => {
    if (tpl.tenant_id === null) {
      Swal.fire({ title: "No permitido", text: "Los templates globales solo los puede eliminar el Super Admin.", icon: "warning" });
      return;
    }
    const result = await Swal.fire({
      title: "¿Eliminar template?",
      text: `"${tpl.name}" será eliminado permanentemente.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#ef4444",
    });
    if (!result.isConfirmed || !tenantId) return;
    const { error } = await supabase.from("invitation_templates").delete().eq("id", tpl.id).eq("tenant_id", tenantId);
    if (error) { Swal.fire({ title: "Error", text: error.message, icon: "error" }); return; }
    setTemplates((prev) => prev.filter((t) => t.id !== tpl.id));
    Swal.fire({ title: "Eliminado", icon: "success", timer: 1200, showConfirmButton: false });
  };

  /* ── Client template CRUD ── */
  const handleSaveCt = async (data: Partial<ClientTemplate>) => {
    if (!tenantId) return;

    if (ctIsNew) {
      const { data: created, error } = await supabase
        .from("client_invitation_templates")
        .insert({ ...data, tenant_id: tenantId })
        .select("*, clients(full_name)")
        .maybeSingle();
      if (error) { Swal.fire({ title: "Error", text: error.message, icon: "error" }); return; }
      if (created) {
        const r = created as Record<string, unknown>;
        setClientTemplates((prev) => [{ ...(r as ClientTemplate), client_name: (r.clients as { full_name: string } | null)?.full_name ?? "—" }, ...prev]);
      }
      Swal.fire({ title: "Asignado", text: "Template personalizado guardado.", icon: "success", timer: 1500, showConfirmButton: false });
    } else if (ctSelected) {
      const { data: updated, error } = await supabase
        .from("client_invitation_templates")
        .update({ ...data, updated_at: new Date().toISOString() })
        .eq("id", ctSelected.id)
        .select("*, clients(full_name)")
        .maybeSingle();
      if (error) { Swal.fire({ title: "Error", text: error.message, icon: "error" }); return; }
      if (updated) {
        const r = updated as Record<string, unknown>;
        setClientTemplates((prev) => prev.map((ct) => ct.id === ctSelected.id ? { ...(r as ClientTemplate), client_name: (r.clients as { full_name: string } | null)?.full_name ?? "—" } : ct));
      }
      Swal.fire({ title: "Guardado", icon: "success", timer: 1500, showConfirmButton: false });
    }
    setCtModalOpen(false);
  };

  const handleDeleteCt = async (ct: ClientTemplate) => {
    const result = await Swal.fire({
      title: "¿Eliminar template?",
      text: `"${ct.name}" de ${ct.client_name} será eliminado.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#ef4444",
    });
    if (!result.isConfirmed || !tenantId) return;
    const { error } = await supabase.from("client_invitation_templates").delete().eq("id", ct.id).eq("tenant_id", tenantId);
    if (error) { Swal.fire({ title: "Error", text: error.message, icon: "error" }); return; }
    setClientTemplates((prev) => prev.filter((t) => t.id !== ct.id));
    Swal.fire({ title: "Eliminado", icon: "success", timer: 1200, showConfirmButton: false });
  };

  const filteredCt = clientTemplates.filter(
    (ct) =>
      ct.name.toLowerCase().includes(search.toLowerCase()) ||
      (ct.client_name ?? "").toLowerCase().includes(search.toLowerCase()),
  );

  const globalTemplates = templates.filter((t) => t.tenant_id === null);
  const ownTemplates    = templates.filter((t) => t.tenant_id !== null);

  return (
    <AdminLayout>
      <AdminHeader
        title="Templates de Invitaciones"
        subtitle="Gestiona fondos genéricos y templates personalizados para clientes"
        actions={
          <div className="flex items-center gap-2">
            <Link
              to="/admin/invitations"
              className="flex items-center gap-1.5 text-slate-500 hover:text-slate-700 border border-slate-200 text-sm px-3 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line"></i></span>
              Volver
            </Link>
            <Link
              to="/admin/invitations/music"
              className="flex items-center gap-1.5 text-slate-500 hover:text-slate-700 border border-slate-200 text-sm px-3 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-music-2-line"></i></span>
              Música
            </Link>
            <button
              onClick={() => {
                if (tab === "generic") { setTplSelected(null); setTplIsNew(true); setTplModalOpen(true); }
                else { setCtSelected(null); setCtIsNew(true); setCtModalOpen(true); }
              }}
              className="flex items-center gap-2 bg-[#C8A96E] hover:bg-[#b8965a] text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line"></i></span>
              {tab === "generic" ? "Nuevo Template" : "Asignar a Cliente"}
            </button>
          </div>
        }
      />

      <ModuleGate moduleId="digital_invitations">
        <div className="px-4 md:px-8 py-6">
          {/* Tabs */}
          <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl w-fit mb-6">
            {([
              { id: "generic", label: "Fondos Genéricos", icon: "ri-image-2-line", count: templates.length },
              { id: "custom",  label: "Personalizados",   icon: "ri-user-star-line", count: clientTemplates.length },
            ] as const).map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  tab === t.id ? "bg-white text-[#0f172a]" : "text-slate-500 hover:text-slate-700"
                }`}
              >
                <span className="w-4 h-4 flex items-center justify-center"><i className={t.icon}></i></span>
                {t.label}
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${tab === t.id ? "bg-slate-100 text-slate-600" : "bg-white/50 text-slate-400"}`}>
                  {t.count}
                </span>
              </button>
            ))}
          </div>

          {loading ? (
            <LoadingState />
          ) : error ? (
            <ErrorState message={error} onRetry={() => tenantId && fetchAll(tenantId)} showLogin />
          ) : (
            <>
              {/* ── GENERIC TEMPLATES ── */}
              {tab === "generic" && (
                <div className="space-y-6">
                  {/* Global (SA) */}
                  {globalTemplates.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <span className="w-5 h-5 flex items-center justify-center text-[#C8A96E]"><i className="ri-global-line text-sm"></i></span>
                        <h3 className="text-sm font-semibold text-[#0f172a]">Templates Globales</h3>
                        <span className="text-xs text-slate-400">(gestionados por Super Admin)</span>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                        {globalTemplates.map((tpl) => (
                          <TemplateCard
                            key={tpl.id}
                            tpl={tpl}
                            isGlobal
                            toggling={togglingId === tpl.id}
                            onEdit={() => { setTplSelected(tpl); setTplIsNew(false); setTplModalOpen(true); }}
                            onToggle={() => handleToggleTpl(tpl)}
                            onDelete={() => handleDeleteTpl(tpl)}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Own */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="w-5 h-5 flex items-center justify-center text-slate-400"><i className="ri-store-line text-sm"></i></span>
                      <h3 className="text-sm font-semibold text-[#0f172a]">Mis Templates</h3>
                      <span className="text-xs text-slate-400">(propios de tu cuenta)</span>
                    </div>
                    {ownTemplates.length === 0 ? (
                      <div className="text-center py-12 border border-dashed border-slate-200 rounded-2xl">
                        <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-slate-100 mx-auto mb-3">
                          <i className="ri-image-add-line text-slate-400 text-xl"></i>
                        </div>
                        <p className="text-slate-500 text-sm font-medium mb-1">Sin templates propios</p>
                        <p className="text-slate-400 text-xs mb-4">Agrega fondos personalizados para tus invitaciones.</p>
                        <button
                          onClick={() => { setTplSelected(null); setTplIsNew(true); setTplModalOpen(true); }}
                          className="text-[#C8A96E] text-sm font-medium hover:underline cursor-pointer"
                        >
                          + Agregar primer template
                        </button>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                        {ownTemplates.map((tpl) => (
                          <TemplateCard
                            key={tpl.id}
                            tpl={tpl}
                            isGlobal={false}
                            toggling={togglingId === tpl.id}
                            onEdit={() => { setTplSelected(tpl); setTplIsNew(false); setTplModalOpen(true); }}
                            onToggle={() => handleToggleTpl(tpl)}
                            onDelete={() => handleDeleteTpl(tpl)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ── CLIENT TEMPLATES ── */}
              {tab === "custom" && (
                <div>
                  {/* Search */}
                  <div className="flex items-center gap-3 mb-5">
                    <div className="relative max-w-sm flex-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-slate-400">
                        <i className="ri-search-line text-sm"></i>
                      </span>
                      <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Buscar por nombre o cliente…"
                        className="w-full border border-slate-200 rounded-lg pl-9 pr-4 py-2.5 text-sm text-[#0f172a] placeholder-slate-400 outline-none focus:border-[#C8A96E]/50 transition-colors"
                      />
                    </div>
                    <span className="text-slate-400 text-sm ml-auto">{filteredCt.length} templates</span>
                  </div>

                  {filteredCt.length === 0 ? (
                    <div className="text-center py-16 border border-dashed border-slate-200 rounded-2xl">
                      <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-slate-100 mx-auto mb-3">
                        <i className="ri-user-star-line text-slate-400 text-2xl"></i>
                      </div>
                      <p className="text-slate-500 font-medium text-sm mb-1">Sin templates personalizados</p>
                      <p className="text-slate-400 text-xs mb-5">Asigna templates exclusivos diseñados para cada cliente.</p>
                      <button
                        onClick={() => { setCtSelected(null); setCtIsNew(true); setCtModalOpen(true); }}
                        className="text-[#C8A96E] text-sm font-medium hover:underline cursor-pointer"
                      >
                        + Asignar primer template
                      </button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                      {filteredCt.map((ct) => (
                        <ClientTemplateCard
                          key={ct.id}
                          ct={ct}
                          onEdit={() => { setCtSelected(ct); setCtIsNew(false); setCtModalOpen(true); }}
                          onDelete={() => handleDeleteCt(ct)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {tplModalOpen && (
          <TemplateCardModal
            template={tplSelected}
            isNew={tplIsNew}
            tenantId={tenantId ?? ""}
            onClose={() => setTplModalOpen(false)}
            onSave={handleSaveTpl}
          />
        )}
        {ctModalOpen && (
          <ClientTemplateModal
            template={ctSelected}
            isNew={ctIsNew}
            clients={clients}
            tenantId={tenantId ?? ""}
            onClose={() => setCtModalOpen(false)}
            onSave={handleSaveCt}
          />
        )}
      </ModuleGate>
    </AdminLayout>
  );
}

/* ─── Sub-components ─────────────────────────────────────────── */
function TemplateCard({
  tpl, isGlobal, toggling, onEdit, onToggle, onDelete,
}: {
  tpl: InvTemplate;
  isGlobal: boolean;
  toggling: boolean;
  onEdit: () => void;
  onToggle: () => void;
  onDelete: () => void;
}) {
  const thumb = tpl.preview_url || tpl.background_url;
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden group">
      {/* Thumb */}
      <div className="relative h-36 bg-slate-100 overflow-hidden">
        {thumb ? (
          <img src={thumb} alt={tpl.name} className="w-full h-full object-cover object-top" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-300">
            <i className="ri-image-line text-3xl"></i>
          </div>
        )}
        {/* Overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all" />
        {/* Badges */}
        <div className="absolute top-2 left-2 flex gap-1">
          {isGlobal && (
            <span className="text-[9px] px-2 py-0.5 rounded-full bg-[#C8A96E]/90 text-white font-semibold">Global</span>
          )}
          {tpl.is_premium && (
            <span className="text-[9px] px-2 py-0.5 rounded-full bg-amber-500/90 text-white font-semibold">Premium</span>
          )}
        </div>
        <div className="absolute top-2 right-2">
          <span className={`text-[9px] px-2 py-0.5 rounded-full font-semibold ${tpl.is_active ? "bg-emerald-100 text-emerald-600" : "bg-slate-100 text-slate-400"}`}>
            {tpl.is_active ? "Activo" : "Inactivo"}
          </span>
        </div>
        {/* Actions overlay */}
        <div className="absolute inset-x-0 bottom-0 p-2 flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={onEdit}
            className="w-7 h-7 flex items-center justify-center bg-white/90 text-slate-700 hover:text-[#C8A96E] rounded-lg text-xs cursor-pointer"
            title="Editar"
          >
            <i className="ri-pencil-line"></i>
          </button>
          {!isGlobal && (
            <button
              onClick={onDelete}
              className="w-7 h-7 flex items-center justify-center bg-white/90 text-slate-700 hover:text-red-500 rounded-lg text-xs cursor-pointer"
              title="Eliminar"
            >
              <i className="ri-delete-bin-line"></i>
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="p-3 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[#0f172a] font-medium text-xs truncate">{tpl.name}</p>
          {tpl.event_types.length > 0 && (
            <p className="text-slate-400 text-[10px] truncate mt-0.5">{tpl.event_types.slice(0, 3).join(" · ")}{tpl.event_types.length > 3 ? " …" : ""}</p>
          )}
        </div>
        <button
          onClick={onToggle}
          disabled={toggling}
          className={`shrink-0 w-8 h-5 rounded-full transition-colors relative cursor-pointer disabled:opacity-50 ${tpl.is_active ? "bg-emerald-400" : "bg-slate-200"}`}
          title={tpl.is_active ? "Desactivar" : "Activar"}
        >
          {toggling ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${tpl.is_active ? "translate-x-3" : "translate-x-0.5"}`} />
          )}
        </button>
      </div>
    </div>
  );
}

function ClientTemplateCard({
  ct, onEdit, onDelete,
}: {
  ct: ClientTemplate;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const thumb = ct.preview_url || ct.template_url;
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden flex gap-0">
      {/* Thumb */}
      <div className="w-24 h-24 shrink-0 bg-slate-100 overflow-hidden">
        {thumb ? (
          <img src={thumb} alt={ct.name} className="w-full h-full object-cover object-top" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-300">
            <i className="ri-user-star-line text-2xl"></i>
          </div>
        )}
      </div>
      {/* Info */}
      <div className="flex-1 p-3 flex flex-col gap-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className="text-[#0f172a] font-medium text-sm truncate">{ct.name}</p>
          <span className={`shrink-0 text-[9px] px-2 py-0.5 rounded-full font-semibold ${ct.is_active ? "bg-emerald-100 text-emerald-600" : "bg-slate-100 text-slate-400"}`}>
            {ct.is_active ? "Activo" : "Inactivo"}
          </span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-slate-400">
          <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-user-3-line text-[11px]"></i></span>
          <span className="truncate">{ct.client_name}</span>
        </div>
        {ct.notes && (
          <p className="text-slate-400 text-[10px] truncate italic">{ct.notes}</p>
        )}
        {ct.portal_token && (
          <div className="flex items-center gap-1 text-[10px] text-slate-300">
            <i className="ri-key-2-line"></i>
            <span className="font-mono truncate">{ct.portal_token.slice(0, 16)}…</span>
          </div>
        )}
        <div className="flex items-center gap-1.5 mt-auto pt-1">
          <button
            onClick={onEdit}
            className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-[#C8A96E] hover:bg-amber-50 rounded-lg transition-all cursor-pointer"
            title="Editar"
          >
            <i className="ri-pencil-line text-xs"></i>
          </button>
          <button
            onClick={onDelete}
            className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-red-400 hover:bg-red-50 rounded-lg transition-all cursor-pointer"
            title="Eliminar"
          >
            <i className="ri-delete-bin-line text-xs"></i>
          </button>
        </div>
      </div>
    </div>
  );
}